import { assertIsDefined, isDefined, isNotDefined } from '@sgme/fp';

import { getMaxDate } from '@/api/dates/utils.ts';
import { optionApi } from '@/api/option/index.ts';
import { createRfsRequestParams } from '@/api/option/requestOptionRfs.ts';
import { createUpdateRfsParams } from '@/api/option/updateOptionRfs.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { tradeCaptureApi } from '@/api/trade-capture/index.ts';
import type { CheckOptionParams } from '@/api/trade-capture/option/checkOption.ts';
import type { OptionTcResponse } from '@/api/trade-capture/option/response.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { areAllExpiryDatesValid, getQuoteType, getRfsId } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { FxOption } from '@/store/option/state.ts';
import { cancelAllOptionsRfsThunk } from '@/store/option/thunks/thunk.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { changeClient } from '@/store/tabs/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerRfsOptionListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: optionApi.endpoints.requestOptionRfs.matchFulfilled,
    effect: async (action, api) => {
      const { rfsId, tileId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }
        api.dispatch(optionApi.endpoints.cancelOptionRfs.initiate({ rfsId, tileId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: optionApi.endpoints.executeOptionRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: optionApi.endpoints.cancelOptionRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;

      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: tradeCaptureApi.endpoints.checkOption.matchFulfilled,
    effect: async (action, api) => {
      const { dispatch, getState } = api;
      const requestPayload: CheckOptionParams = action.meta.arg.originalArgs;
      const tradeCaptureResponse: OptionTcResponse = action.payload;
      if (tradeCaptureResponse.tradeCapture?.status !== 'CHECKED') {
        return;
      }

      const context: RfsContext = getRfsContext({ dispatch, getState, requestPayload });
      if (context.type === 'invalid') {
        return;
      }

      // isPriceObsolete = true means a price WAS previously sent but is now stale (can be refreshed via update)
      // isPriceObsolete = false means no price exists yet on this stream (needs a new RFS)
      // isProductObsolete = true means the product structure changed (existing stream is invalid, needs a new RFS)
      const { isReadyToPrice, isPriceObsolete, isProductObsolete } = tradeCaptureResponse.tradeCapture;
      const needsNewRfs = !isPriceObsolete || isProductObsolete;

      const rfsId = getRfsId(context.getOption());
      if (isDefined(rfsId)) {
        if (isProductObsolete) {
          // Product changed: the existing RFS stream is invalid, cancel it. A new RFS will be requested below if isReadyToPrice
          await cancelRfs(context, rfsId);
        } else if (isPriceObsolete && isReadyToPrice) {
          // Price is stale but product is the same: refresh the existing stream
          await updateRfs(context, rfsId);
          return;
        } else {
          // Either RFS is still valid (isReadyToPrice && !isPriceObsolete) or not ready to price yet (!isReadyToPrice)
          return;
        }
      }

      // Here: no existing RFS, or we just canceled it (isProductObsolete)
      await refreshMaxDate(context);

      if (isReadyToPrice && needsNewRfs && areAllExpiryDatesValid(context.getOption())) {
        await requestRfs(context);
      }
    },
  });

  startAppListening({
    matcher: changeClient.match,
    effect: async (_, api) => {
      await api.dispatch(cancelAllOptionsRfsThunk());
    },
  });
  startAppListening({
    matcher: optionSlice.actions.changeClient.match,
    effect: async ({ payload }, api) => {
      const { tileId } = payload;
      const option = optionSlice.selectors.getOptionTile(api.getState(), tileId);
      const rfsId = isDefined(option) ? getRfsId(option) : undefined;
      if (isDefined(rfsId)) {
        await api.dispatch(
          optionApi.endpoints.cancelOptionRfs.initiate({
            tileId: tileId,
            rfsId: rfsId,
            removeQuote: true,
          }),
        );
      }
    },
  });
};

type RfsContext = ValidRfsContext | InvalidRfsContext;
interface InvalidRfsContext {
  type: 'invalid';
}

interface ValidRfsContext {
  type: 'valid';
  getState(): AppState;
  getOption(): FxOption;
  dispatch: AppDispatch;
  tileId: string;
}

function getRfsContext(opts: {
  getState(): AppState;
  dispatch: AppDispatch;
  requestPayload: CheckOptionParams;
}): RfsContext {
  const { getState, dispatch, requestPayload } = opts;
  const tileId = requestPayload.tileId;
  const option = getState().option[tileId];
  if (isNotDefined(option) || isDefined(option.execution)) {
    return { type: 'invalid' };
  }
  return {
    type: 'valid',
    getState,
    getOption: () => {
      // the tile could have been closed since this context was created, this is called lazily from listener callbacks
      const option = getState().option[tileId];
      assertIsDefined(option, `option tile ${tileId} was removed while its RFS was still in progress`);

      return option;
    },
    dispatch,
    tileId,
  };
}

async function cancelRfs(context: ValidRfsContext, rfsId: string) {
  const { dispatch, tileId } = context;
  await dispatch(optionApi.endpoints.cancelOptionRfs.initiate({ tileId, rfsId, removeQuote: true }));
}

async function updateRfs(context: ValidRfsContext, rfsId: string) {
  const { dispatch, tileId, getOption } = context;
  const option = getOption();
  const updateParams = createUpdateRfsParams(option, tileId, rfsId);
  await dispatch(optionApi.endpoints.updateOptionRfs.initiate(updateParams));
}

async function refreshMaxDate(context: ValidRfsContext) {
  const { dispatch, tileId, getOption, getState } = context;
  const option = getOption();
  const clientId = selectTileClientId(getState(), tileId);
  const currentUserCache = selectGetCurrentUser(getState()).data?.clients;
  const maxDate = await getMaxDate(clientId, currentUserCache, dispatch, option.properties.currencyPair);
  dispatch(optionSlice.actions.setMaxDate({ tileId, maxDate }));
}

async function requestRfs(context: ValidRfsContext) {
  const { dispatch, tileId, getState, getOption } = context;

  const option = getOption();
  const newRfsId = getNewRfsId();
  const clientId = selectTileClientId(getState(), tileId);
  const forceTradeable = getQuoteType(option) === 'Tradeable';
  const rfsRequestParams = createRfsRequestParams({
    option: option,
    tileId: tileId,
    rfsId: newRfsId,
    clientId: clientId,
    forceTradeable: forceTradeable,
  });
  await dispatch(optionApi.endpoints.requestOptionRfs.initiate(rfsRequestParams, { fixedCacheKey: newRfsId }));
}
